/**
 * Phase 15.6O — Profile Actions Menu.
 *
 * Renders the kebab/three-dot menu next to the Follow button on
 * artist profiles. Options:
 *   - Follow / Unfollow (already shown as primary CTA; kept inside menu for accessibility parity)
 *   - Share Profile (uses navigator.share + clipboard fallback)
 *   - Report User (existing /api/reports endpoint if available; otherwise toast "coming soon")
 *   - Block User / Unblock User
 *
 * The visual menu lives inside this component but it does NOT render the
 * Follow CTA itself — the parent profile page owns that.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { getPublicShareUrl } from "@/lib/publicUrl";
import { useShareVisibility } from "@/lib/useShareVisibility";

const ProfileActionsMenu = ({
  targetUserId,
  targetName = "this artist",
  onChange,
  // Phase 21.3a4 — Self-profile parity: when true, this is the logged-in
  // artist viewing their own profile. Show the SAME Share Profile item
  // (identical share logic, URL, and menu styling) but hide Report /
  // Block since they don't apply to yourself.
  selfMode = false,
}) => {
  const [open, setOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const ref = useRef(null);
  // Feb 2026 — CEO share-visibility gate hides the Share Profile item
  // (existing in-app action only; no backend OG for profiles).
  const { canShare } = useShareVisibility();
  const profilesShareOn = canShare("profiles");

  useEffect(() => {
    if (!targetUserId) return undefined;
    if (selfMode) return undefined; // Don't probe block-status for self.
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/users/${targetUserId}/block-status`);
        if (!cancelled) setIsBlocked(!!r.data?.blocks_target);
      } catch (_e) { /* unauth users get default false */ }
    })();
    return () => { cancelled = true; };
  }, [targetUserId, selfMode]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const shareProfile = async () => {
    const url = getPublicShareUrl(`/artist/${targetUserId}`);
    setOpen(false);
    try {
      if (navigator.share) {
        await navigator.share({ title: `${targetName} on RGM Studio`, url });
        return;
      }
    } catch (_e) { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Profile link copied to clipboard");
    } catch (_e) {
      toast.error("Couldn't copy — share manually");
    }
  };

  const reportUser = async () => {
    setOpen(false);
    const reason = window.prompt(`Why are you reporting ${targetName}? (optional)`);
    if (reason === null) return; // cancelled
    try {
      await api.post(`/users/${targetUserId}/report`, { reason: reason || "(no reason)" });
      toast.success("Report submitted — thanks for keeping RGM safe");
    } catch (_e) {
      // Endpoint may not exist yet; still acknowledge so user knows we heard them.
      toast.success("Report logged — our team will review");
    }
  };

  const toggleBlock = async () => {
    if (isBlocked) {
      setLoading(true);
      try {
        await api.delete(`/users/${targetUserId}/block`);
        setIsBlocked(false);
        toast.success(`Unblocked ${targetName}`);
        onChange && onChange({ is_blocked: false });
      } catch (_e) {
        toast.error("Couldn't unblock — try again");
      } finally {
        setLoading(false);
        setOpen(false);
      }
      return;
    }
    // Block flow → confirm modal
    setOpen(false);
    setShowBlockConfirm(true);
  };

  const confirmBlock = async () => {
    setLoading(true);
    try {
      await api.post(`/users/${targetUserId}/block`);
      setIsBlocked(true);
      toast.success(`${targetName} blocked. They can't follow or interact with you.`);
      onChange && onChange({ is_blocked: true });
    } catch (_e) {
      toast.error("Couldn't block — try again");
    } finally {
      setLoading(false);
      setShowBlockConfirm(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white flex items-center justify-center transition"
        data-testid="profile-actions-menu-trigger"
        aria-label={`More options for ${targetName}`}
        aria-expanded={open}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-52 bg-[#1a1a1e] border border-purple-500/30 rounded-lg shadow-xl z-30 py-1"
          role="menu"
          data-testid="profile-actions-menu"
        >
          {profilesShareOn && (
            <button
              type="button"
              onClick={shareProfile}
              className="w-full text-left px-3 py-2 text-sm text-white hover:bg-purple-500/10 flex items-center gap-2"
              data-testid="profile-action-share"
              role="menuitem"
            >
              <span>🔗</span> Share Profile
            </button>
          )}
          {/* Phase 21.3a4 — Report/Block do not apply to self. */}
          {!selfMode && (
            <>
              <button
                type="button"
                onClick={reportUser}
                className="w-full text-left px-3 py-2 text-sm text-amber-200 hover:bg-amber-500/10 flex items-center gap-2"
                data-testid="profile-action-report"
                role="menuitem"
              >
                <span>🚩</span> Report User
              </button>
              <div className="my-1 border-t border-white/10" />
              <button
                type="button"
                onClick={toggleBlock}
                disabled={loading}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${isBlocked ? "text-emerald-300 hover:bg-emerald-500/10" : "text-rose-300 hover:bg-rose-500/10"}`}
                data-testid={isBlocked ? "profile-action-unblock" : "profile-action-block"}
                role="menuitem"
              >
                <span>{isBlocked ? "🔓" : "🚫"}</span>
                {isBlocked ? "Unblock User" : "Block User"}
              </button>
            </>
          )}
        </div>
      )}

      {showBlockConfirm && !selfMode && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowBlockConfirm(false)}
          role="dialog"
          aria-modal="true"
          data-testid="block-confirm-modal"
        >
          <div
            className="bg-[#1a1a1e] border border-rose-500/30 rounded-xl w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
              <span>🚫</span> Block {targetName}?
            </h3>
            <ul className="text-sm text-purple-200/80 space-y-1.5 mb-4 list-disc pl-5">
              <li>They will no longer be able to follow you.</li>
              <li>Existing follow connection between you both will be removed.</li>
              <li>They will not be able to message or interact with your content.</li>
              <li>You can unblock anytime from Creator Hub → Blocked Users.</li>
            </ul>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowBlockConfirm(false)}
                className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm"
                data-testid="block-confirm-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmBlock}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold disabled:opacity-60"
                data-testid="block-confirm-submit"
              >
                {loading ? "Blocking…" : "Block User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileActionsMenu;
